import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import DesktopLayout from '../../../layout/DesktopLayout';
import BottomNav from '../../../BottomNav';
import { useDesktopTopBar } from '../../../../context/DesktopLayoutContext';
import { useBranch } from '../../../../context/BranchContext';
import { useIsDesktop } from '../../../reports/reportTokens';
import { useMediaLibraryStore } from '../../../../redux/display/mediaLibrary/mediaLibraryHooks';
import { usePlaylistStore } from '../../../../redux/display/playlist/playlistHooks';
import { useDisplayToastStore } from '../../../../redux/display/display/displayHooks';
import { Icon } from '../../components/ui/Icon/Icon';
import { OrientationIcon } from '../../components/ui/OrientationIcon/OrientationIcon';
import { DisplayToastContainer } from '../../components/ui/DisplayToastContainer/DisplayToastContainer';
import { MediaCard } from '../../components/data/MediaCard/MediaCard';
import { buildDisplayMediaSrc } from '../../../../services/displayApi';
import { displayPaths } from '../../paths';
import { useFormDiscardGuard } from '../../../productCatalog/hooks/useFormDiscardGuard';
import { DeleteConfirmationModal } from '../../components/ui/DeleteConfirmationModal/DeleteConfirmationModal';
import type { DisplayOrientation, MediaLibraryItem } from '../../data/types';
import './PlaylistFormPage.css';

const BORDER = 'var(--card-border, #E2E5EC)';
const T1 = 'var(--t1, #1A1D2E)';
const T2 = 'var(--t2, #475569)';
const T3 = 'var(--t3, #9CA3B8)';
const ACCENT = 'var(--accent, #D23A3B)';
const RED = 'var(--red, #DC2626)';
const FONT = 'var(--font-primary, DM Sans, sans-serif)';

const panelStyle: React.CSSProperties = { 
  background: 'var(--surface, #fff)', 
  border: `1px solid ${BORDER}`, 
  borderRadius: 14, 
  padding: 20,
  flex: '1 0 auto',
  minHeight: 340,
};
const inputStyle: React.CSSProperties = { width: '100%', padding: '10px 14px', borderRadius: 8, border: `1px solid ${BORDER}`, fontSize: 13, fontFamily: FONT, color: T1, outline: 'none', boxSizing: 'border-box' };

/** Same grouped-segment visual as Media Library's filter tabs and orientation pickers, for UI consistency across the module. */
function OrientationToggle({ value, onChange, disabled }: { value: DisplayOrientation; onChange: (v: DisplayOrientation) => void; disabled?: boolean }) {
  const options: DisplayOrientation[] = ['Portrait', 'Landscape'];
  return (
    <div style={{ display: 'flex', gap: 6, background: 'var(--hover, #F0F1F5)', border: `1px solid ${BORDER}`, borderRadius: 8, padding: 4, flexShrink: 0 }}>
      {options.map((opt) => {
        const active = value === opt;
        return (
          <button
            key={opt}
            type="button"
            disabled={disabled}
            onClick={() => onChange(opt)}
            style={{
              display: 'flex', alignItems: 'center', gap: 6, border: 'none', cursor: disabled ? 'not-allowed' : 'pointer',
              padding: '7px 14px', borderRadius: 6, fontSize: 12, fontWeight: 600, fontFamily: FONT,
              background: active ? ACCENT : 'transparent', color: active ? '#fff' : T2,
              opacity: disabled && !active ? 0.6 : 1,
              transition: 'all .15s',
            }}
          >
            <OrientationIcon orientation={opt} size={14} color={active ? '#fff' : T2} />
            {opt}
          </button>
        );
      })}
    </div>
  );
}

/** Reorderable list of the selected media, in play order — drag handle + thumbnail + name,
 *  mirroring the drag-reorder pattern used for Item variations in Product Catalog. */
function PlayOrderList({
  orderedItems, dragId, onDragStart, onDragEnd, onDrop, onRemove,
}: {
  orderedItems: MediaLibraryItem[];
  dragId: string | null;
  onDragStart: (id: string) => void;
  onDragEnd: () => void;
  onDrop: (targetId: string) => void;
  onRemove: (id: string) => void;
}) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {orderedItems.map((item, index) => (
        <div
          key={item.id}
          draggable
          onDragStart={() => onDragStart(item.id)}
          onDragEnd={onDragEnd}
          onDragOver={(e) => e.preventDefault()}
          onDrop={() => onDrop(item.id)}
          style={{
            display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px',
            borderRadius: 10, border: `1px solid ${BORDER}`, background: 'var(--surface, #fff)',
            opacity: dragId === item.id ? 0.5 : 1, cursor: 'grab', transition: 'opacity .15s ease',
          }}
        >
          <Icon name="drag_indicator" size={18} color={T3} />
          <span style={{
            flexShrink: 0, width: 20, height: 20, borderRadius: '50%', background: 'var(--hover, #F0F1F5)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700, color: T2, fontFamily: FONT,
          }}>
            {index + 1}
          </span>
          <div style={{ width: 36, height: 36, borderRadius: 8, overflow: 'hidden', flexShrink: 0, background: 'linear-gradient(180deg, #172033 0%, #101827 100%)' }}>
            {item.kind === 'video' ? (
              <video src={buildDisplayMediaSrc(item.id, item.mimeType) ?? item.url} muted preload="metadata" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            ) : (
              <img src={buildDisplayMediaSrc(item.id, item.mimeType) ?? item.url} alt={item.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            )}
          </div>
          <span style={{ flex: 1, minWidth: 0, fontSize: 13, fontWeight: 600, color: T1, fontFamily: FONT, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={item.name}>
            {item.name}
          </span>
          <button
            type="button"
            onClick={() => onRemove(item.id)}
            aria-label={`Remove ${item.name}`}
            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4, display: 'flex', flexShrink: 0 }}
          >
            <Icon name="close" size={16} color={T3} />
          </button>
        </div>
      ))}
    </div>
  );
}

export default function PlaylistFormPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isDesktop = useIsDesktop();
  const isMobile = !isDesktop;
  const setTopBarProps = useDesktopTopBar();
  const { selectedBranch } = useBranch();
  const { items: mediaItems, fetchMedia } = useMediaLibraryStore();
  const { items: playlists, fetchPlaylists, createPlaylist, updatePlaylist } = usePlaylistStore();
  const { addToast } = useDisplayToastStore();
  const [isSaving, setIsSaving] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const currentLocationId = selectedBranch?.id ?? (selectedBranch as any)?.locationId ?? '';

  const editId = searchParams.get('id');
  const existingPlaylist = useMemo(() => playlists.find((p) => p.id === editId), [playlists, editId]);
  const isEditMode = Boolean(editId && existingPlaylist);

  const [orientation, setOrientation] = useState<DisplayOrientation>('Portrait');
  const [name, setName] = useState('');
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [errors, setErrors] = useState<{ name?: string; media?: string }>({});
  const [dragId, setDragId] = useState<string | null>(null);

  const isFormDirty = useMemo(() => {
    if (isEditMode && existingPlaylist) {
      if (name.trim() !== existingPlaylist.name.trim()) return true;
      if (orientation !== existingPlaylist.orientation) return true;
      const originalIds = [...existingPlaylist.mediaEntries]
        .sort((a, b) => a.sequenceNo - b.sequenceNo)
        .map((e) => e.mediaId);
      if (selectedIds.length !== originalIds.length) return true;
      for (let i = 0; i < selectedIds.length; i++) {
        if (selectedIds[i] !== originalIds[i]) return true;
      }
      return false;
    } else {
      if (name.trim() !== '') return true;
      if (selectedIds.length > 0) return true;
      return false;
    }
  }, [isEditMode, existingPlaylist, name, orientation, selectedIds]);

  const isDirty = isFormDirty && !isSaving && !isSubmitted;

  const {
    showDiscardModal,
    handleCancel,
    handleConfirmDiscard,
    handleKeepEditing,
  } = useFormDiscardGuard(isDirty, `${displayPaths.mediaLibrary}?tab=playlists`);

  useEffect(() => { setTopBarProps({ title: isEditMode ? 'Edit Playlist' : 'Create Playlist', breadcrumbs: 'Display / Media Library' }); }, [setTopBarProps, isEditMode]);
  useEffect(() => { if (currentLocationId) { fetchMedia(currentLocationId); fetchPlaylists(currentLocationId); } }, [fetchMedia, fetchPlaylists, currentLocationId]);

  useEffect(() => {
    if (existingPlaylist) {
      setOrientation(existingPlaylist.orientation);
      setName(existingPlaylist.name);
      const idsInOrder = [...existingPlaylist.mediaEntries]
        .sort((a, b) => a.sequenceNo - b.sequenceNo)
        .map((e) => e.mediaId);
      setSelectedIds(idsInOrder);
    }
  }, [existingPlaylist]);

  const eligibleMedia = useMemo(() => mediaItems.filter((m) => m.orientation === orientation), [mediaItems, orientation]);

  const handleOrientationChange = (next: DisplayOrientation) => {
    setOrientation(next);
    setSelectedIds([]);
  };

  const toggleMedia = (id: string) => {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]));
    if (errors.media) setErrors((e) => ({ ...e, media: undefined }));
  };

  const removeSelected = (id: string) => setSelectedIds((prev) => prev.filter((i) => i !== id));

  const reorderSelected = (targetId: string) => {
    if (!dragId || dragId === targetId) return;
    setSelectedIds((prev) => {
      const from = prev.indexOf(dragId);
      const to = prev.indexOf(targetId);
      if (from < 0 || to < 0) return prev;
      const next = [...prev];
      const [moved] = next.splice(from, 1);
      next.splice(to, 0, moved);
      return next;
    });
  };

  const selectedItemsInOrder = useMemo(
    () => selectedIds.map((id) => mediaItems.find((m) => m.id === id)).filter((m): m is MediaLibraryItem => Boolean(m)),
    [selectedIds, mediaItems],
  );

  const goBackToPlaylists = () => {
    handleCancel();
  };

  const handleSave = () => {
    const nextErrors: { name?: string; media?: string } = {};
    if (!name.trim()) nextErrors.name = 'Playlist name is required';
    // Only show the "select media" validation when there's actually media to select — if the
    // library has none for this orientation, the empty-state guidance below already covers it.
    if (selectedIds.length === 0 && eligibleMedia.length > 0) nextErrors.media = 'Select at least one media item';
    if (Object.keys(nextErrors).length > 0 || selectedIds.length === 0) {
      setErrors(nextErrors);
      return;
    }
    if (!currentLocationId) {
      addToast('No outlet selected — pick a location first.', 'error');
      return;
    }

    setIsSaving(true);
    const save = isEditMode && existingPlaylist
      ? updatePlaylist(existingPlaylist.id, currentLocationId, name.trim(), orientation, selectedIds)
      : createPlaylist(currentLocationId, name.trim(), orientation, selectedIds);

    save
      .then(() => {
        setIsSubmitted(true);
        addToast(isEditMode ? 'Playlist updated' : 'Playlist created', 'success');
        navigate(`${displayPaths.mediaLibrary}?tab=playlists`);
      })
      .catch((err: Error) => {
        addToast(err.message || 'Failed to save playlist', 'error');
      })
      .finally(() => setIsSaving(false));
  };

  const content = (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        minHeight: isMobile ? '100vh' : '100%',
        width: '100%',
        boxSizing: 'border-box',
      }}
    >
      {/* Scrollable Form Content */}
      <div
        style={{
          flex: 1,
          padding: isMobile ? '12px 16px 20px' : '28px 28px 24px',
          display: 'flex',
          flexDirection: 'column',
          gap: isMobile ? 14 : 20,
          boxSizing: 'border-box',
        }}
      >
        {!isMobile && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <button type="button" onClick={goBackToPlaylists} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4, display: 'flex' }}>
              <Icon name="arrow_back" size={20} color={T2} />
            </button>
            <div>
              <div style={{ fontSize: 22, fontWeight: 700, color: T1, fontFamily: FONT }}>{isEditMode ? 'Edit Playlist' : 'Create Playlist'}</div>
              <div style={{ fontSize: 13, color: T3, marginTop: 2, fontFamily: FONT }}>
                {isEditMode ? 'Update this playlist’s name, orientation, or media.' : 'Group media from your library into a schedulable playlist.'}
              </div>
            </div>
          </div>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 12, fontWeight: 600, color: T2, fontFamily: FONT }}>Orientation</span>
            <OrientationToggle value={orientation} onChange={handleOrientationChange} disabled={isEditMode} />
          </div>
          {isEditMode && (
            <div style={{ fontSize: 11, color: T2, fontFamily: FONT }}>
              <strong style={{ fontWeight: 700 }}>Note:</strong> Orientation can't be changed after a playlist is created.
            </div>
          )}
        </div>

        <div style={{ display: 'flex', gap: 20, alignItems: 'flex-start', flexWrap: 'wrap' }}>
          <div style={{ flex: '1 1 480px', minWidth: 0, display: 'flex', flexDirection: 'column', gap: 18, order: isMobile ? 1 : 0 }}>
            <div style={panelStyle}>
              <div style={{ fontSize: 11, fontWeight: 600, color: T3, textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 8, fontFamily: FONT }}>
                Playlist Name <span style={{ color: RED }}>*</span>
              </div>
              <input
                type="text"
                value={name}
                onChange={(e) => {
                  setName(e.target.value);
                  if (errors.name) setErrors((prev) => ({ ...prev, name: undefined }));
                }}
                placeholder="e.g. Weekend Specials"
                style={{ ...inputStyle, border: `1px solid ${errors.name ? RED : BORDER}` }}
              />
              {errors.name && <div style={{ fontSize: 11, color: RED, marginTop: 6, fontFamily: FONT }}>{errors.name}</div>}

              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 18, marginBottom: 8 }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: T3, textTransform: 'uppercase', letterSpacing: 0.4, fontFamily: FONT }}>
                  Select Media ({orientation})
                </div>
                <span style={{ fontSize: 11, color: T3, fontFamily: FONT, marginLeft: 'auto' }}>{selectedIds.length} selected</span>
              </div>
              {eligibleMedia.length === 0 ? (
                <div style={{ fontSize: 12, color: T3, fontFamily: FONT, padding: '12px 0' }}>
                  No {orientation.toLowerCase()} media available — upload some from the Media Library first.
                </div>
              ) : (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 12, maxHeight: 'calc(100vh - 380px)', overflowY: 'auto', paddingRight: 4 }}>
                  {eligibleMedia.map((item) => (
                    <MediaCard
                      key={item.id}
                      item={item}
                      checkable
                      selected={selectedIds.includes(item.id)}
                      onPreview={() => toggleMedia(item.id)}
                    />
                  ))}
                </div>
              )}
              {errors.media && <div style={{ fontSize: 11, color: RED, marginTop: 10, fontFamily: FONT }}>{errors.media}</div>}
            </div>
          </div>

          <div
            style={{
              flex: isMobile ? '1 1 100%' : '0 0 420px', minWidth: isMobile ? undefined : 360, display: 'flex', flexDirection: 'column', gap: 18,
              position: isMobile ? undefined : 'sticky', top: isMobile ? undefined : 20,
              order: isMobile ? 0 : 1,
            }}
          >
            <div style={{ ...panelStyle, minHeight: 'calc(100vh - 360px)', display: 'flex', flexDirection: 'column' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: T3, textTransform: 'uppercase', letterSpacing: 0.4, fontFamily: FONT }}>
                  Play Order
                </div>
                {selectedItemsInOrder.length > 0 && (
                  <button
                    type="button"
                    onClick={() => setSelectedIds([])}
                    style={{ marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer', padding: 0, fontSize: 11, fontWeight: 600, color: RED, fontFamily: FONT }}
                  >
                    Clear all
                  </button>
                )}
              </div>
              {selectedItemsInOrder.length > 0 && (
                <div style={{ fontSize: 12, color: T3, marginBottom: 12, fontFamily: FONT }}>
                  Drag to reorder — this is the sequence media will play in.
                </div>
              )}
              {selectedItemsInOrder.length === 0 ? (
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 10, textAlign: 'center', padding: '12px 0' }}>
                  <Icon name="playlist_play" size={28} color={T3} />
                  <div style={{ fontSize: 12, color: T3, fontFamily: FONT }}>
                    Select media from the library to build the play order.
                  </div>
                </div>
              ) : (
                <div style={{ maxHeight: 'calc(100vh - 380px)', overflowY: 'auto', paddingRight: 4 }}>
                  <PlayOrderList
                    orderedItems={selectedItemsInOrder}
                    dragId={dragId}
                    onDragStart={setDragId}
                    onDragEnd={() => setDragId(null)}
                    onDrop={reorderSelected}
                    onRemove={removeSelected}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Sticky Bottom Action Footer Bar - Flush at bottom */}
      <div
        style={{
          position: 'sticky',
          bottom: 0,
          zIndex: 100,
          background: 'var(--surface, #fff)',
          borderTop: `1px solid ${BORDER}`,
          boxShadow: '0 -4px 16px rgba(0, 0, 0, 0.05)',
          padding: isMobile ? '12px 16px' : '14px 28px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
          gap: 12,
          width: '100%',
          boxSizing: 'border-box',
        }}
      >
        <button
          type="button"
          onClick={goBackToPlaylists}
          style={{
            padding: '10px 22px',
            borderRadius: 10,
            border: `1px solid ${BORDER}`,
            background: 'var(--surface, #fff)',
            color: T2,
            fontSize: 13,
            fontWeight: 600,
            cursor: 'pointer',
            fontFamily: FONT,
            transition: 'all .15s ease',
          }}
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={handleSave}
          disabled={isSaving}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            padding: '10px 24px',
            borderRadius: 10,
            border: 'none',
            background: ACCENT,
            color: '#fff',
            fontSize: 13,
            fontWeight: 600,
            cursor: isSaving ? 'default' : 'pointer',
            opacity: isSaving ? 0.7 : 1,
            fontFamily: FONT,
            boxShadow: '0 2px 8px rgba(210,58,59,0.25)',
            transition: 'all .15s ease',
          }}
        >
          <Icon name={isEditMode ? 'check' : 'add'} size={16} color="#fff" />
          {isSaving ? 'Saving...' : isEditMode ? 'Save Changes' : 'Create Playlist'}
        </button>
      </div>

      <DeleteConfirmationModal
        open={showDiscardModal}
        onClose={handleKeepEditing}
        onConfirm={handleConfirmDiscard}
        title="Discard changes?"
        description="You have unsaved changes. Are you sure you want to discard them?"
        confirmLabel="Discard"
        cancelLabel="Keep Editing"
      />

      <DisplayToastContainer />
    </div>
  );

  if (isMobile) {
    return (
      <div style={{ background: 'var(--bg, #F5F6FA)', minHeight: '100vh', fontFamily: FONT, overflowX: 'hidden', width: '100%', boxSizing: 'border-box' }}>
        {content}
        <BottomNav />
      </div>
    );
  }

  return (
    <DesktopLayout>
      <div style={{ width: '100%', minWidth: 0, height: '100%', minHeight: 'calc(100vh - 64px)', display: 'flex', flexDirection: 'column', background: 'var(--bg, #F5F6FA)', boxSizing: 'border-box', fontFamily: FONT }}>
        {content}
      </div>
    </DesktopLayout>
  );
}
